import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, ShieldCheck, Clock, FileWarning, Plus, Pencil, Trash2, Wrench, Bell } from "lucide-react";
import { useCanDelete } from "@/context/AuthContext";

const EMPTY = {
  title: "", category: "licence", authority: "", entity_type: "company",
  entity_id: "", entity_name: "", document_no: "",
  issue_date: "", expiry_date: "", renewal_reminder_days: 30,
  file_url: "", fee_paid: 0, notes: "",
};

const CATEGORIES = [
  { v: "licence", l: "Licence" },
  { v: "permit", l: "Permit" },
  { v: "insurance", l: "Insurance" },
  { v: "certificate", l: "Certificate" },
  { v: "tax_filing", l: "Tax filing (GST/TDS)" },
  { v: "inspection", l: "Inspection" },
  { v: "other", l: "Other" },
];

export default function Compliance() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["compliance"],
    queryFn: () => api.get("/compliance/dashboard").then((r) => r.data),
    refetchInterval: 60000,
  });
  const scan = useMutation({
    mutationFn: () => api.post("/compliance/scan-now"),
    onSuccess: (r) => {
      toast.success(`Scanned ${r.data.processed} items → ${r.data.notifications_created} new notifications sent`);
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Scan failed"),
  });

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Compliance & PESO</h1>
          <p className="text-sm text-muted-foreground">Auto-tracked expiries · Manual records · Service due alerts. Daily scheduler notifies admin/accounts/manager users.</p>
        </div>
        <Button onClick={() => scan.mutate()} disabled={scan.isPending} data-testid="btn-compliance-scan-now">
          <Bell size={14} className="mr-1" /> {scan.isPending ? "Scanning…" : "Scan & notify now"}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KPI icon={ShieldCheck} label="Total bowsers" value={data?.summary?.total_bowsers || 0} />
        <KPI icon={AlertTriangle} label="Expired" value={data?.summary?.expired_count || 0} highlight />
        <KPI icon={Clock} label="Expiring soon" value={data?.summary?.expiring_count || 0} />
        <KPI icon={FileWarning} label="PESO alerts" value={data?.summary?.peso_alert_count || 0} />
        <KPI icon={ShieldCheck} label="Manual records" value={data?.summary?.manual_records || 0} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="records" data-testid="tab-records">Records</TabsTrigger>
          <TabsTrigger value="service" data-testid="tab-service"><Wrench size={14} className="mr-1" /> Service Due</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Section title="⚠ Expired items" items={data?.expired} colorClass="text-status-cancelled" />
          <Section title="Expiring soon (≤ 60 days)" items={data?.expiring_soon} colorClass="text-status-pending" />
          <Section title="PESO calibration alerts (≤ 90 days)" items={data?.peso_alerts} colorClass="text-primary" />
          {data?.manual_alerts?.length > 0 && <ManualAlerts items={data.manual_alerts} />}
          {data?.driver_expiries?.length > 0 && <DriverExpiries items={data.driver_expiries} />}
        </TabsContent>

        <TabsContent value="records" className="mt-4"><RecordsPanel /></TabsContent>
        <TabsContent value="service" className="mt-4"><ServiceDuePanel /></TabsContent>
      </Tabs>
    </div>
  );
}

const KPI = ({ icon: Icon, label, value, highlight }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground">
      <div className="text-xs uppercase tracking-widest">{label}</div>
      <Icon size={16} />
    </div>
    <div className={`mt-2 font-display text-2xl font-black ${highlight ? "text-status-cancelled" : ""}`}>{value}</div>
  </div>
);

function Section({ title, items = [], colorClass }) {
  if (!items || items.length === 0) return null;
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-4 py-2">Bowser</th><th className="px-4 py-2">Reg. no</th><th className="px-4 py-2">Compliance</th><th className="px-4 py-2">Expiry</th><th className="px-4 py-2 text-right">Days</th></tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={`${r.bowser_id}-${r.compliance_type}`} className="border-b border-border/40 text-sm">
                <td className="px-4 py-2 font-mono">{r.bowser_code}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.registration}</td>
                <td className="px-4 py-2">{r.compliance_type}</td>
                <td className="px-4 py-2 text-xs">{r.expiry_date}</td>
                <td className={`px-4 py-2 text-right font-mono font-bold ${colorClass}`}>{r.days_left}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function DriverExpiries({ items }) {
  return (
    <Card>
      <CardHeader><CardTitle>Driver licence expiries</CardTitle></CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Name</th><th className="px-4 py-2">Licence</th><th className="px-4 py-2">Expiry</th><th className="px-4 py-2 text-right">Days left</th></tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.driver_id} className="border-b border-border/40 text-sm">
                <td className="px-4 py-2 font-mono">{d.driver_code}</td>
                <td className="px-4 py-2">{d.name}</td>
                <td className="px-4 py-2 font-mono text-xs">{d.license_no}</td>
                <td className="px-4 py-2 text-xs">{d.expiry_date}</td>
                <td className={`px-4 py-2 text-right font-mono font-bold ${d.days_left < 0 ? "text-status-cancelled" : "text-status-pending"}`}>{d.days_left}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ManualAlerts({ items }) {
  return (
    <Card>
      <CardHeader><CardTitle>Manual compliance alerts</CardTitle></CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Title</th><th className="px-4 py-2">Category</th><th className="px-4 py-2">Authority</th><th className="px-4 py-2">Expiry</th><th className="px-4 py-2 text-right">Days</th></tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-border/40 text-sm">
                <td className="px-4 py-2 font-mono">{r.code}</td>
                <td className="px-4 py-2 font-semibold">{r.title}</td>
                <td className="px-4 py-2 text-xs uppercase">{r.category}</td>
                <td className="px-4 py-2 text-xs">{r.authority || "—"}</td>
                <td className="px-4 py-2 text-xs">{r.expiry_date || "—"}</td>
                <td className={`px-4 py-2 text-right font-mono font-bold ${r.days_left < 0 ? "text-status-cancelled" : "text-status-pending"}`}>{r.days_left}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function RecordsPanel() {
  const qc = useQueryClient();
  const canDelete = useCanDelete();
  const { data } = useQuery({ queryKey: ["compliance-records"], queryFn: () => api.get("/compliance/records").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e });

  const save = useMutation({
    mutationFn: () => (editingId ? api.patch(`/compliance/records/${editingId}`, form) : api.post("/compliance/records", form)),
    onSuccess: () => { toast.success(editingId ? "Record updated" : "Record added"); qc.invalidateQueries({ queryKey: ["compliance-records"] }); qc.invalidateQueries({ queryKey: ["compliance"] }); setOpen(false); setEditingId(null); setForm(EMPTY); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/compliance/records/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["compliance-records"] }); qc.invalidateQueries({ queryKey: ["compliance"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditingId(r.id); setOpen(true); };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle>Manual compliance records</CardTitle>
          <div className="text-xs text-muted-foreground mt-1">Licences, permits, tax filings, certificates & inspections not tied to a bowser/driver.</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="btn-new-compliance"><Plus size={14} className="mr-1" /> New record</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editingId ? "Edit compliance record" : "New compliance record"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={upd("title")} placeholder="PESO Storage Licence" data-testid="cmp-title" /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={upd("category")}>
                  <SelectTrigger data-testid="cmp-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Issuing authority</Label><Input value={form.authority} onChange={upd("authority")} placeholder="PESO Nagpur / MPCB / etc." /></div>
              <div>
                <Label>Entity type</Label>
                <Select value={form.entity_type} onValueChange={upd("entity_type")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="bowser">Bowser</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="facility">Facility / Depot</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Entity ref (name/code)</Label><Input value={form.entity_name} onChange={upd("entity_name")} placeholder="e.g. HQ, BW-01, Rajesh" /></div>
              <div><Label>Document / Certificate No</Label><Input value={form.document_no} onChange={upd("document_no")} data-testid="cmp-docno" /></div>
              <div><Label>Fee paid (₹)</Label><Input type="number" step="0.01" value={form.fee_paid} onChange={upd("fee_paid")} /></div>
              <div><Label>Issue date</Label><Input type="date" value={(form.issue_date || "").slice(0, 10)} onChange={upd("issue_date")} /></div>
              <div><Label>Expiry date</Label><Input type="date" value={(form.expiry_date || "").slice(0, 10)} onChange={upd("expiry_date")} data-testid="cmp-expiry" /></div>
              <div><Label>Renewal reminder (days before)</Label><Input type="number" value={form.renewal_reminder_days} onChange={upd("renewal_reminder_days")} /></div>
              <div><Label>File / attachment URL</Label><Input value={form.file_url} onChange={upd("file_url")} placeholder="https://…" /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={upd("notes")} /></div>
            </div>
            <DialogFooter><Button onClick={() => save.mutate()} disabled={!form.title || save.isPending} data-testid="btn-save-compliance">{save.isPending ? "Saving…" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-2">Code</th>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2">Entity</th>
              <th className="px-4 py-2">Doc No</th>
              <th className="px-4 py-2">Expiry</th>
              <th className="px-4 py-2 text-right">Days left</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).map((r) => (
              <tr key={r.id} className="border-b border-border/40 text-sm" data-testid={`cmp-row-${r.code}`}>
                <td className="px-4 py-2 font-mono">{r.code}</td>
                <td className="px-4 py-2 font-semibold">{r.title}</td>
                <td className="px-4 py-2 text-xs uppercase">{r.category}</td>
                <td className="px-4 py-2 text-xs">{r.entity_name || r.entity_type}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.document_no || "—"}</td>
                <td className="px-4 py-2 text-xs">{r.expiry_date || "—"}</td>
                <td className={`px-4 py-2 text-right font-mono ${r.days_left < 0 ? "font-bold text-status-cancelled" : r.days_left <= (r.renewal_reminder_days || 30) ? "text-status-pending" : ""}`}>
                  {r.expiry_date ? r.days_left : "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`cmp-edit-${r.code}`}><Pencil size={12} /></Button>
                    {canDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`cmp-del-${r.code}`}><Trash2 size={12} /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete {r.title}?</AlertDialogTitle><AlertDialogDescription>This will permanently remove {r.code}. Only admins can delete. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del.mutate(r.id)} data-testid={`cmp-del-confirm-${r.code}`}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {(!data?.items || data.items.length === 0) && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">No manual records yet. Add licences, permits, tax filings, and certificates.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ServiceDuePanel() {
  const { data } = useQuery({ queryKey: ["service-due"], queryFn: () => api.get("/fleet/service-due").then((r) => r.data) });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KPI icon={AlertTriangle} label="Overdue" value={data?.summary?.overdue || 0} highlight />
        <KPI icon={Clock} label="Due in 30 days / 500 km" value={data?.summary?.due_soon || 0} />
        <KPI icon={Wrench} label="Open breakdowns" value={data?.summary?.open_breakdowns || 0} />
      </div>

      <Card>
        <CardHeader><CardTitle>Service due bowsers</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Bowser</th>
                <th className="px-4 py-2">Reg. no</th>
                <th className="px-4 py-2">Service type</th>
                <th className="px-4 py-2">Last service</th>
                <th className="px-4 py-2 text-right">Current km</th>
                <th className="px-4 py-2 text-right">Next due km</th>
                <th className="px-4 py-2">Next due date</th>
                <th className="px-4 py-2 text-right">Days</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.due || []).map((r) => (
                <tr key={r.bowser_id} className="border-b border-border/40 text-sm" data-testid={`sd-row-${r.bowser_code}`}>
                  <td className="px-4 py-2 font-mono">{r.bowser_code}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.registration}</td>
                  <td className="px-4 py-2 text-xs uppercase">{r.service_type.replace(/_/g, " ")}</td>
                  <td className="px-4 py-2 text-xs">{r.last_service_date}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.current_km?.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.next_due_km ? r.next_due_km.toLocaleString() : "—"}</td>
                  <td className="px-4 py-2 text-xs">{r.next_due_date || "—"}</td>
                  <td className={`px-4 py-2 text-right font-mono font-bold ${r.days_left < 0 ? "text-status-cancelled" : "text-status-pending"}`}>{r.days_left}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`rounded-full px-2 py-0.5 font-semibold uppercase ${r.status === "overdue" ? "bg-status-cancelled/10 text-status-cancelled" : "bg-status-pending/10 text-status-pending"}`}>{r.status.replace(/_/g, " ")}</span>
                  </td>
                </tr>
              ))}
              {(!data?.due || data.due.length === 0) && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">✓ All bowsers up to date on scheduled service.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {data?.open_breakdowns?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-status-cancelled" /> Open breakdowns</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-2">Bowser</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Description</th><th className="px-4 py-2">Vendor</th><th className="px-4 py-2 text-right">Cost</th><th className="px-4 py-2">Status</th></tr>
              </thead>
              <tbody>
                {data.open_breakdowns.map((b) => (
                  <tr key={b.id} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-2 font-mono">{b.bowser_code}</td>
                    <td className="px-4 py-2 text-xs">{b.date?.slice(0, 10)}</td>
                    <td className="px-4 py-2 text-xs">{b.description}</td>
                    <td className="px-4 py-2 text-xs">{b.vendor || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">₹{(b.cost || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2 text-xs uppercase">{b.status?.replace(/_/g, " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
